/**
 * Delete on the course list, which archives.
 *
 * An archived course is hidden from the list and from students and can be
 * restored (it comes back as an unpublished draft). A course that students
 * can see, or that is still being built, may only go after the admin types
 * DELETE — the server enforces that with a 409 and this file decides what to
 * say about it.
 *
 * Pure: no React, no network, so the decisions and the words can be tested
 * directly. Every sentence a person reads here is ours; the server's text is
 * written for API callers and never reaches the screen.
 */
// Type-only: erased at build, so this pure file never loads the REST client.
import type {ArchiveResult} from '../../Hooks/LmsCourseApi/useCourseList';
import type {CourseState} from './courseStatus';

/** What the admin types to delete a published or still-building course. */
export const CONFIRM_WORD = 'DELETE';

export type ArchiveReason = 'published' | 'building' | 'other';

export type ArchiveRisk = {
  video_course_id: number;
  title: string;
  reason: ArchiveReason;
};

/** The part of a list row these decisions read. */
export type ArchivableRow = {
  video_course_id: number;
  title: string;
  status?: CourseState | null;
  /** Not in the list payload today; read if the server starts sending it. */
  is_public?: boolean | null;
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const courses = (n: number) => `${n} ${plural(n, 'course', 'courses')}`;

function countOf(value: unknown): number | null {
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

/**
 * What the list itself already tells us needs a typed confirm.
 *
 * Queued counts as building: the server stops a queued job as well as a
 * running one, and asking once too often costs a word, while not asking costs
 * a build. Published is only knowable when the row carries it; otherwise the
 * server's 409 says so and the dialog switches then.
 */
export function knownRisks(rows: ArchivableRow[]): ArchiveRisk[] {
  const risks: ArchiveRisk[] = [];
  for (const row of rows) {
    const building = row.status === 'pending' || row.status === 'running';
    if (building) {
      risks.push({video_course_id: row.video_course_id, title: row.title, reason: 'building'});
    } else if (row.is_public === true) {
      risks.push({video_course_id: row.video_course_id, title: row.title, reason: 'published'});
    }
  }
  return risks;
}

/**
 * The courses a 409 says need confirming, or null when the error is not that
 * question. An unknown reason is kept — the server still wants a confirm —
 * but described in our words.
 */
export function needsConfirmFrom(error: unknown): ArchiveRisk[] | null {
  const response = (error as any)?.response;
  if (response?.status !== 409 || response?.data?.error !== 'confirm_required') {
    return null;
  }
  const list = Array.isArray(response.data.needs_confirm) ? response.data.needs_confirm : [];
  return list
    .filter((item: any) => item && Number.isFinite(Number(item.video_course_id)))
    .map((item: any) => {
      const id = Number(item.video_course_id);
      const reason: ArchiveReason =
        item.reason === 'published' || item.reason === 'building' ? item.reason : 'other';
      const title =
        typeof item.title === 'string' && item.title.trim() ? item.title.trim() : `Course ${id}`;
      return {video_course_id: id, title, reason};
    });
}

export function riskText(reason: ArchiveReason): string {
  if (reason === 'published') return 'Published — students will lose access';
  if (reason === 'building') return 'Still building — the build will be stopped';
  return 'Needs confirming before it can be deleted';
}

export function deleteLabel(n: number): string {
  return n > 0 ? `Delete ${courses(n)}` : 'Delete';
}

export function restoreLabel(n: number): string {
  return n > 0 ? `Restore ${courses(n)}` : 'Restore';
}

export function selectedLabel(n: number): string {
  return n > 0 ? `${n} selected` : 'None selected';
}

export function dialogTitle(n: number): string {
  return n === 1 ? 'Delete this course?' : `Delete ${n} courses?`;
}

export function dialogBody(n: number): string {
  return n === 1
    ? "It'll move to Archived — you can restore it."
    : "They'll move to Archived — you can restore them.";
}

/** "Archived 3 Oct 2026", or just "Archived" when the date is missing. */
export function archivedOn(iso: string | null | undefined): string {
  if (!iso) return 'Archived';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'Archived';
  return `Archived ${at.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
}

export function archivedToast(result: ArchiveResult | null | undefined, requested: number): string {
  const n = countOf(result?.archived) ?? requested;
  const stopped = countOf(result?.cancelled) ?? 0;
  const head = `${courses(n)} archived`;
  return stopped > 0
    ? `${head} · ${stopped} ${plural(stopped, 'build', 'builds')} stopped`
    : head;
}

/** What Undo should restore: what the server archived, or what was asked. */
export function archivedIds(
  result: ArchiveResult | null | undefined,
  requested: number[],
): number[] {
  const archived = result?.archived;
  if (Array.isArray(archived) && archived.every(id => typeof id === 'number')) {
    return archived;
  }
  return requested;
}

export function restoredToast(n: number): string {
  return `${courses(n)} restored ${plural(n, 'as a draft', 'as drafts')}`;
}

export function describeArchiveError(error: unknown, kind: 'archive' | 'restore'): string {
  const response = (error as any)?.response;
  const verb = kind === 'archive' ? 'delete' : 'restore';
  if (!response) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  if (response.status === 403) return `Only admins can ${verb} courses.`;
  return `Couldn't ${verb} those courses. Try again.`;
}
